<template>
  <el-dialog
    title="派发财务"
    :visible="visible"
    width="520px"
    :destroy-on-close="true"
    :lock-scroll="false"
    append-to-body
    custom-class="ele-dialog-form outorder-handle-dialog"
    @update:visible="updateVisible">
    <el-form ref="form" :model="form" :rules="rules" label-width="80px" size="small">
      <div class="order-summary">
        <div class="order-summary__item">
          <span class="order-summary__label">系统订单</span>
          <span class="order-summary__value">{{ order.orderNo }}</span>
        </div>
        <div class="order-summary__item">
          <span class="order-summary__label">金额</span>
          <span class="order-summary__value">{{ orderAmountText }}</span>
        </div>
      </div>

      <el-form-item label="推送方式" prop="push_type">
        <el-select v-model="form.push_type" class="ele-fluid" clearable placeholder="请选择">
          <el-option
            v-for="item in pushTypeOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value" />
        </el-select>
      </el-form-item>

      <el-form-item label="付款信息" prop="resource_column_ids">
        <el-select
          v-model="form.resource_column_ids"
          multiple
          clearable
          filterable
          class="ele-fluid"
          popper-class="outorder-handle-select-dropdown"
          placeholder="请选择"
          :loading="listLoading"
          :filter-method="filterAccounts">
          <el-option
            v-for="item in filteredAccountList"
            :key="item.id"
            :value="item.id"
            :label="tagLabel(item)">
            <div class="dispatch-option">
              <div class="dispatch-option__left">
                <div class="dispatch-option__finance">
                  <span>{{ item.finance_name }}</span>
                  <span v-if="item.id === recommendedId" class="dispatch-option__tag">推荐</span>
                </div>
                <div class="dispatch-option__sub">{{ item.account_info }}</div>
              </div>
              <div class="dispatch-option__amount" :class="{ cover: Number(item.can_cover) === 1 }">
                {{ item.sj_amount }}
              </div>
            </div>
          </el-option>
        </el-select>
      </el-form-item>

      <el-form-item v-if="splitPreview.length" label="拆分明细">
        <div class="split-box">
          <div class="split-head">
            <span class="col-finance">财务</span>
            <span class="col-info">收款信息</span>
            <span class="col-bal">实际余额</span>
            <span class="col-amt">分摊金额</span>
          </div>
          <div v-for="item in splitPreview" :key="item.id" class="split-row">
            <span class="col-finance" :title="item.finance_name">{{ item.finance_name }}</span>
            <span class="col-info" :title="item.account_info">{{ item.account_info }}</span>
            <span class="col-bal">{{ item.balance }}</span>
            <span class="col-amt">{{ item.amount }}</span>
          </div>
        </div>
      </el-form-item>
    </el-form>

    <div slot="footer">
      <el-button @click="updateVisible(false)">取消</el-button>
      <el-button type="primary" :loading="loading" :disabled="submitDisabled" @click="submit">确定</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { loadDictOptions } from '@/utils/dict';

export default {
  name: 'OutorderHandleDialog',
  props: {
    visible: Boolean,
    order: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: false,
      listLoading: false,
      accountList: [],
      filteredAccountList: [],
      pushTypeOptions: [],
      form: {
        push_type: '',
        resource_column_ids: []
      },
      rules: {
        push_type: [{ required: true, message: '请选择', trigger: 'change' }],
        resource_column_ids: [{ type: 'array', required: true, min: 1, message: '请选择', trigger: 'change' }]
      }
    };
  },
  created() {
    loadDictOptions('dispatch_push_type', { numberValue: true })
      .then((list) => {
        this.pushTypeOptions = list || [];
      })
      .catch(() => {
        this.pushTypeOptions = [
          { value: 1, label: '客户端' },
          { value: 2, label: '机器人' }
        ];
      });
  },
  computed: {
    orderAmount() {
      return Number(this.order && this.order.paymentAmount != null ? this.order.paymentAmount : 0);
    },
    orderAmountText() {
      return this.order && this.order.paymentAmount != null ? this.order.paymentAmount : '0';
    },
    recommendedId() {
      const amount = Math.round(this.orderAmount * 100) / 100;
      if (amount <= 0) return null;
      let bestId = null;
      let bestDiff = Infinity;
      (this.accountList || []).forEach((item) => {
        const bal = Math.round(Number(item.sj_amount || 0) * 100) / 100;
        if (bal < amount) return;
        const diff = Math.round((bal - amount) * 100) / 100;
        if (diff < bestDiff) {
          bestDiff = diff;
          bestId = item.id;
        }
      });
      return bestId;
    },
    accountMap() {
      const map = {};
      (this.accountList || []).forEach((item) => {
        map[item.id] = item;
      });
      return map;
    },
    splitPreview() {
      const ids = this.form.resource_column_ids || [];
      if (!ids.length) return [];
      const amount = Math.round(this.orderAmount * 100) / 100;
      if (amount <= 0) {
        return ids.map((id) => {
          const item = this.accountMap[id] || {};
          return {
            id,
            finance_name: item.finance_name,
            account_info: item.account_info,
            balance: item.sj_amount,
            amount: '0.00'
          };
        });
      }
      let remain = amount;
      const rows = [];
      ids.forEach((id) => {
        if (remain <= 0.009) return;
        const item = this.accountMap[id];
        if (!item) return;
        const bal = Math.round(Number(item.sj_amount || 0) * 100) / 100;
        if (bal <= 0) return;
        const use = Math.round(Math.min(bal, remain) * 100) / 100;
        if (use <= 0) return;
        rows.push({
          id,
          finance_name: item.finance_name,
          account_info: item.account_info,
          balance: item.sj_amount,
          amount: use.toFixed(2)
        });
        remain = Math.round((remain - use) * 100) / 100;
      });
      return rows;
    },
    splitUsed() {
      return this.splitPreview.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    },
    splitRemain() {
      if (this.orderAmount <= 0) return 0;
      return Math.round((this.orderAmount - this.splitUsed) * 100) / 100;
    },
    submitDisabled() {
      return this.listLoading || !this.form.resource_column_ids.length || this.splitRemain > 0.01;
    }
  },
  watch: {
    visible(val) {
      this.loading = false;
      this.form.push_type = '';
      this.form.resource_column_ids = [];
      if (!val) {
        this.listLoading = false;
        this.accountList = [];
        this.filteredAccountList = [];
        return;
      }
      this.loadAccounts();
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.clearValidate();
      });
    }
  },
  methods: {
    updateVisible(value) {
      this.$emit('update:visible', value);
    },
    tagLabel(item) {
      return `${item.finance_name}(${item.sj_amount})`;
    },
    filterAccounts(query) {
      const q = String(query == null ? '' : query).trim().toLowerCase();
      if (!q) {
        this.filteredAccountList = this.accountList.slice();
        return;
      }
      this.filteredAccountList = this.accountList.filter((item) => {
        const haystack = [
          item.finance_name,
          item.account_info,
          item.account_name,
          item.bank_name,
          item.account_number,
          item.sj_amount
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.indexOf(q) !== -1;
      });
    },
    loadAccounts() {
      if (!this.order || !this.order.id) {
        this.accountList = [];
        this.filteredAccountList = [];
        return;
      }
      this.listLoading = true;
      this.$http
        .get('/Outorder/getDispatchAccounts', { params: { id: this.order.id } })
        .then((res) => {
          this.listLoading = false;
          if (res.data.code === 0) {
            this.accountList = res.data.data || [];
            this.filteredAccountList = this.accountList.slice();
            return;
          }
          this.accountList = [];
          this.filteredAccountList = [];
          this.$message.error(res.data.msg || '获取付款信息失败');
        })
        .catch((e) => {
          this.listLoading = false;
          this.accountList = [];
          this.filteredAccountList = [];
          this.$message.error(e.message);
        });
    },
    submit() {
      this.$refs.form.validate((valid) => {
        if (!valid || this.submitDisabled) return false;
        if (!this.order || !this.order.id) return;
        this.loading = true;
        this.$http
          .post('/Outorder/handle', {
            id: this.order.id,
            push_type: this.form.push_type,
            resource_column_ids: this.form.resource_column_ids
          })
          .then((res) => {
            this.loading = false;
            if (res.data.code === 0) {
              this.$message.success(res.data.msg || '派单成功');
              this.updateVisible(false);
              this.$emit('done');
              return;
            }
            this.$message.error(res.data.msg || '派单失败');
          })
          .catch((e) => {
            this.loading = false;
            this.$message.error(e.message);
          });
      });
    }
  }
};
</script>

<style scoped>
.order-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 24px;
  margin: 0 0 18px;
  padding: 12px 14px;
  background: #f5f7fa;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}
.order-summary__item {
  min-width: 180px;
  line-height: 1.5;
}
.order-summary__label {
  display: inline-block;
  margin-right: 8px;
  color: #909399;
}
.order-summary__value {
  color: #303133;
  word-break: break-all;
}
.dispatch-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  line-height: 1.35;
  white-space: normal;
  word-break: break-all;
}
.dispatch-option__left {
  min-width: 0;
  flex: 1;
}
.dispatch-option__finance {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #303133;
}
.dispatch-option__tag {
  display: inline-block;
  padding: 0 5px;
  border-radius: 2px;
  background: #ecf5ff;
  border: 1px solid #d9ecff;
  color: #409eff;
  font-size: 11px;
  font-weight: 500;
  line-height: 16px;
}
.dispatch-option__sub {
  margin-top: 2px;
  color: #909399;
  font-size: 11px;
}
.dispatch-option__amount {
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: #e6a23c;
  white-space: nowrap;
}
.dispatch-option__amount.cover {
  color: #67c23a;
}
.split-box {
  width: 100%;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  overflow: hidden;
  background: #fff;
}
.split-head,
.split-row {
  display: grid;
  grid-template-columns: 100px 1fr 90px 90px;
  gap: 8px;
  align-items: center;
  padding: 8px 12px;
  font-size: 12px;
  line-height: 1.4;
}
.split-head {
  background: #f5f7fa;
  color: #909399;
  border-bottom: 1px solid #ebeef5;
}
.split-row {
  color: #606266;
  border-bottom: 1px solid #f2f6fc;
}
.split-row:last-of-type {
  border-bottom: none;
}
.col-finance,
.col-info {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.col-bal,
.col-amt {
  text-align: right;
  white-space: nowrap;
}
.col-amt {
  color: #409eff;
  font-weight: 600;
}
</style>

<style>
.outorder-handle-dialog .el-select__tags {
  max-width: 100%;
}
.outorder-handle-select-dropdown .el-select-dropdown__item {
  height: auto !important;
  min-height: 40px;
  line-height: normal !important;
  padding: 8px 14px;
  white-space: normal;
  border-bottom: 1px solid #f2f6fc;
}
.outorder-handle-select-dropdown .el-select-dropdown__item:last-child {
  border-bottom: none;
}
.outorder-handle-select-dropdown .el-select-dropdown__item.hover,
.outorder-handle-select-dropdown .el-select-dropdown__item:hover {
  background: #f5f7fa;
}
.outorder-handle-select-dropdown .el-select-dropdown__item.selected {
  background: #ecf5ff;
}
.outorder-handle-select-dropdown.el-select-dropdown {
  max-width: 480px;
  padding: 4px 0;
}
</style>
